import { ImageUp, Landmark, Pencil, Plus, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BANK_LOGO_ACCEPT,
  MAX_BANK_LOGO_BYTES,
  useBanks,
  useDeleteBankLogo,
  useSaveBank,
  useUploadBankLogo,
  type Bank,
  type BankInput,
} from './banks'
import { Footer } from './components/AccountForms'
import { BankLogo } from './components/banks/BankLogo'
import { Section } from './components/FinanceBits'
import { CheckRow, Field, FormSection } from './components/FormBits'
import { toastError } from './ui'

/** Merkezi banka listesi: hesap, kart, kredi ve nakit avans banka seçimleri buradan; logo listelerde görünür. */
export function BanksPage() {
  const { t } = useTranslation()
  const banks = useBanks(true)
  const upload = useUploadBankLogo()
  const removeLogo = useDeleteBankLogo()
  const [editing, setEditing] = useState<{ bank: Bank | null } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [logoFor, setLogoFor] = useState<Bank | null>(null)

  const pickLogo = (b: Bank) => {
    setLogoFor(b)
    fileRef.current?.click()
  }
  const onFile = (file: File | undefined) => {
    if (!file || !logoFor) return
    if (file.size > MAX_BANK_LOGO_BYTES) {
      toast.error(t('finance.banks.logoTooBig'))
      return
    }
    upload.mutate({ id: logoFor.id, file }, { onSuccess: () => toast.success(t('finance.banks.logoSaved')), onError: toastError })
  }

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.banks.title')}
        description={t('finance.banks.subtitle')}
        actions={
          <Button onClick={() => setEditing({ bank: null })}>
            <Plus />
            {t('finance.banks.add')}
          </Button>
        }
      />
      <input
        ref={fileRef}
        type="file"
        accept={BANK_LOGO_ACCEPT}
        className="hidden"
        onChange={(e) => {
          onFile(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      <Section title={t('finance.banks.listTitle')} icon={Landmark} bodyClassName="p-0">
        {banks.isError ? (
          <ErrorState error={banks.error} onRetry={() => void banks.refetch()} />
        ) : banks.isLoading ? (
          <Skeleton className="m-4 h-48" />
        ) : !banks.data?.length ? (
          <EmptyState icon={Landmark} title={t('finance.banks.empty')} />
        ) : (
          <ul className="divide-y">
            {banks.data.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <span className="bg-muted/40 flex size-11 shrink-0 items-center justify-center rounded-lg border">
                  <BankLogo url={b.logoUrl} name={b.name} className="size-8" />
                </span>
                <div className="min-w-0 flex-1 basis-56">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {b.shortName || b.name}
                    {!b.active ? <Badge tone="neutral">{t('finance.banks.inactive')}</Badge> : null}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {b.name}
                    {b.eftCode ? ` · EFT ${b.eftCode}` : ''}
                    {b.swift ? ` · SWIFT ${b.swift}` : ''}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" disabled={upload.isPending} onClick={() => pickLogo(b)}>
                    <ImageUp />
                    {t(b.logoUrl ? 'finance.banks.changeLogo' : 'finance.banks.uploadLogo')}
                  </Button>
                  {b.logoUrl ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t('finance.banks.removeLogo')}
                      title={t('finance.banks.removeLogo')}
                      onClick={() =>
                        void confirmModal({ title: t('finance.banks.removeLogo'), description: b.name, confirmLabel: t('common.delete'), destructive: true }).then(
                          (ok) => ok && removeLogo.mutate(b.id, { onSuccess: () => toast.success(t('finance.banks.logoRemoved')), onError: toastError }),
                        )
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ) : null}
                  <Button size="icon" variant="ghost" aria-label={t('common.edit')} title={t('common.edit')} onClick={() => setEditing({ bank: b })}>
                    <Pencil className="size-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
      {editing ? <BankForm bank={editing.bank} onDone={() => setEditing(null)} /> : null}
    </>
  )
}

function BankForm({ bank, onDone }: { bank: Bank | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveBank()
  const [v, setV] = useState<BankInput>({
    name: bank?.name ?? '',
    shortName: bank?.shortName ?? '',
    eftCode: bank?.eftCode ?? '',
    swift: bank?.swift ?? '',
    active: bank?.active ?? true,
  })
  const [touched, setTouched] = useState(false)
  const set = (patch: Partial<BankInput>) => setV((x) => ({ ...x, ...patch }))
  const errors = {
    name: v.name.trim().length < 2 ? t('finance.banks.form.nameRequired') : '',
    eft: v.eftCode.trim() && !/^\d{4,5}$/.test(v.eftCode.trim()) ? t('finance.banks.form.eftInvalid') : '',
    swift: v.swift.trim() && !/^[A-Za-z]{6}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/.test(v.swift.trim()) ? t('finance.banks.form.swiftInvalid') : '',
  }
  const valid = Object.values(errors).every((e) => !e)
  const err = (k: keyof typeof errors) => (touched ? errors[k] || undefined : undefined)
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onDone()}
      dismissible={!save.isPending}
      icon={<Landmark />}
      title={t(bank ? 'finance.banks.form.editTitle' : 'finance.banks.form.createTitle')}
      onSubmit={(e) => {
        e.preventDefault()
        setTouched(true)
        if (!valid) return
        save.mutate(
          { id: bank?.id, input: v },
          {
            onSuccess: () => {
              toast.success(t('finance.banks.form.saved'))
              onDone()
            },
            onError: toastError,
          },
        )
      }}
      footer={<Footer pending={save.isPending} onCancel={onDone} />}
    >
      <FormSection>
        <Field id="bk-name" label={t('finance.banks.form.name')} error={err('name')} full>
          <Input id="bk-name" maxLength={120} value={v.name} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field id="bk-short" label={t('finance.banks.form.shortName')}>
          <Input id="bk-short" maxLength={40} value={v.shortName} onChange={(e) => set({ shortName: e.target.value })} />
        </Field>
        <Field id="bk-eft" label={t('finance.banks.form.eft')} error={err('eft')} hint={t('finance.banks.form.eftHint')}>
          <Input id="bk-eft" inputMode="numeric" maxLength={5} value={v.eftCode} onChange={(e) => set({ eftCode: e.target.value })} />
        </Field>
        <Field id="bk-swift" label="SWIFT / BIC" error={err('swift')}>
          <Input id="bk-swift" className="uppercase" maxLength={11} value={v.swift} onChange={(e) => set({ swift: e.target.value })} />
        </Field>
        <div className="sm:col-span-2">
          <CheckRow checked={v.active} onChange={(e) => set({ active: e.target.checked })} hint={t('finance.banks.form.activeHint')}>
            {t('finance.banks.form.active')}
          </CheckRow>
        </div>
      </FormSection>
    </Modal>
  )
}
